import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import TransactionTimeline from '../components/TransactionDetail/TransactionTimeline';
import TransactionApprovals from '../components/TransactionDetail/TransactionApprovals';
import TransactionInfo from '../components/TransactionDetail/TransactionInfo';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const TransactionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'approvals'>('overview');

  const { data: transaction, isLoading, error } = useQuery({
    queryKey: ['transaction', id],
    queryFn: () => apiClient.getTransaction(id!),
    enabled: !!id,
    refetchInterval: 30000, // Refetch every 30 seconds for live updates
  });

  // Mock timeline data - in real app this would come from API
  const mockTimelineEvents = [
    {
      id: '1',
      type: 'initiated' as const,
      title: 'Transaction Initiated',
      description: 'User initiated bridge transaction on source chain',
      timestamp: transaction?.createdAt || new Date().toISOString(),
      txHash: transaction?.requestTxHash,
      blockNumber: transaction?.requestBlockNumber,
      status: 'completed' as const
    },
    {
      id: '2',
      type: 'validated' as const,
      title: 'Transaction Validated',
      description: 'Transaction validated and added to pending queue',
      timestamp: transaction?.createdAt || new Date().toISOString(),
      status: transaction?.status === 'executed' ? 'completed' as const : 
             transaction?.status === 'approved' ? 'current' as const : 'pending' as const
    },
    {
      id: '3',
      type: 'signed' as const,
      title: 'Multi-Signature Collection',
      description: 'Collecting validator signatures for cross-chain execution',
      timestamp: transaction?.createdAt || new Date().toISOString(),
      status: transaction?.status === 'executed' ? 'completed' as const :
             transaction?.status === 'approved' ? 'current' as const : 'pending' as const
    },
    {
      id: '4',
      type: 'executed' as const,
      title: 'Target Chain Execution',
      description: 'Transaction executed on target chain',
      timestamp: transaction?.updatedAt || new Date().toISOString(),
      txHash: transaction?.executeTxHash,
      blockNumber: transaction?.executeBlockNumber,
      status: transaction?.status === 'executed' ? 'completed' as const : 'pending' as const
    }
  ];

  

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Transaction ID</h1>
          <button
            onClick={() => navigate('/transactions')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Transaction Not Found</h1>
          <p className="text-gray-600 mb-4">
            The transaction with ID "{id}" could not be found.
          </p>
          <button
            onClick={() => navigate('/transactions')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'timeline', name: 'Timeline', icon: '🕐' },
    { id: 'approvals', name: 'Approvals', icon: '✅' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-500 hover:text-gray-700"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Transaction Details
                </h1>
                <p className="text-sm text-gray-500">
                  ID: {transaction.id}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                transaction.status === 'executed' ? 'bg-green-100 text-green-800' :
                transaction.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                transaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
              </span>
              
              <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <TransactionInfo transaction={transaction} />
        )}
        
        {activeTab === 'timeline' && (
          <TransactionTimeline events={mockTimelineEvents} />
        )}
        
        {activeTab === 'approvals' && (
          <TransactionApprovals
            approvals={transaction.approvals.map(i => ({
              validator: i.validator,
              signature: i.txHash,
              timestamp: new Date(i.timestamp * 1000).toISOString(),
              status: 'approved'
            }))}
            requiredApprovals={transaction.requiredApprovals}
          />
        )}
      </div>
    </div>
  );
};

export default TransactionDetail; 